/**
 * Renders a load's three states once, so no screen invents its own spinner or
 * swallows its own failure. A failed content read is stated in plain words: the
 * application is expected to be launched offline and must say what is missing.
 */
import type { JSX, ReactNode } from 'react';
import type { Async } from '../hooks/use-async.js';

export function AsyncBoundary<T>({
  result,
  label,
  children,
}: {
  readonly result: Async<T>;
  /** What is being loaded, in a sentence: "the mission catalog". */
  readonly label: string;
  readonly children: (value: T) => ReactNode;
}): JSX.Element {
  if (result.status === 'loading') {
    return (
      <p role="status" className="text-sm text-ink-muted" data-testid="loading">
        Loading {label}…
      </p>
    );
  }
  if (result.status === 'error') {
    return (
      <div
        role="alert"
        data-testid="load-error"
        className="rounded-lg border border-danger bg-danger-soft p-4 text-sm text-ink"
      >
        <p className="font-medium">Could not load {label}.</p>
        <p className="mt-1 text-ink-muted">{result.error.message}</p>
      </div>
    );
  }
  return <>{children(result.value)}</>;
}
